import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

import { AssignmentOperatePage } from './pages/assignment/operate/assignment-operate.page';
import { AssignmentPage } from './pages/assignment/assignment.page';
import { AssignmentSignPage } from './pages/assignment/sign/assignment-sign.page';
import { ContractAdvicePage } from './pages/contract/advice/contract-advice.page';
import { ContractFormPage } from './pages/contract/form/contract-form.page';
import { ContractLandingPage } from './pages/contract/landing/contract-landing.page';
import { ContractMinorDisabledPage } from './pages/contract/minor-disabled/contract-minor-disabled.page';
import { ContractNaturePage } from './pages/contract/nature/contract-nature.page';
import { ContractNaturalPage } from './pages/contract/natural/contract-natural.page';
import { ContractPage } from './pages/contract/contract.page';
import { ContractParticipantsPage } from './pages/contract/participants/contract-participants.page';
import { ContractSignPage } from './pages/contract/sign/contract-sign.page';
import { ContributionOperatePage } from './pages/contribution/operate/contribution-operate.page';
import { ContributionPage } from './pages/contribution/contribution.page';
import { ContributionSignPage } from './pages/contribution/sign/contribution-sign.page';
import { DictionaryDialog } from './ui/dialog/dictionary/dictionary.dialog';
import { DisinvestmentOperatePage } from './pages/disinvestment/operate/disinvestment-operate.page';
import { DisinvestmentPage } from './pages/disinvestment/disinvestment.page';
import { DisinvestmentSignPage } from './pages/disinvestment/sign/disinvestment-sign.page';
import { FullDataAccountPage } from './pages/full-data/account/full-data-account.page';
import { FullDataListPage } from './pages/full-data/list/full-data-list.page';
import { FullDataPage } from './pages/full-data/full-data.page';
import { FullDataProfilePage } from './pages/full-data/profile/full-data-profile.page';
import { NotFoundPage } from './pages/error/not-found/not-found.page';
import { MeetingSignPage } from './pages/operative/meeting-sign/meeting-sign.page';
import { OperativeListPage } from './pages/operative/list/operative-list.page';
import { OperativeOperatePage } from './pages/operative/operate/operative-operate.page';
import { OperativePage } from './pages/operative/operative.page';
import { OperativeProductDetailPage } from './pages/operative/product/detail/operative-product-detail.page';
import { OperativeProductListPage } from './pages/operative/product/list/operative-product-list.page';
import { OperativeSignPage } from './pages/operative/sign/operative-sign.page';
import { PhoneDialog } from './ui/dialog/phone/phone.dialog';
import { RepeatTestPage } from './pages/repeat-test/repeat-test.page';
import { RepeatTestQuestionPage } from './pages/repeat-test/question/repeat-test-question.page';
import { RepeatTestResultPage } from './pages/repeat-test/result/repeat-test-result.page';
import { RepeatTestResultSuitabilityPage } from './pages/repeat-test/result-suitability/repeat-test-result-suitability.page';
import { RepeatTestSignHandwritingPage } from './pages/repeat-test/sign-handwriting/repeat-test-sign-handwriting.page';
import { RepeatTestSignPage } from './pages/repeat-test/sign/repeat-test-sign.page';
import { TestPage } from './pages/test/test.page';
import { TestQuestionPage } from './pages/test/question/test-question.page';
import { TestResultPage } from './pages/test/result/test-result.page';
import { TestResultSuitabilityPage } from './pages/test/result-suitability/test-result-suitability.page';
import { TestRatePage } from './pages/test/rate/test-rate.page';
import { TestTransferPage } from './pages/test/transfer/test-transfer.page';
import { UnauthorizedPage } from './pages/error/unauthorized/unauthorized.page';
import { UserAuthenticated } from './client/helpers/user-authenticated';
import { ViewAccountPage } from './pages/view/account/view-account.page';
import { ViewPage } from './pages/view/view.page';
import { ViewProfilePage } from './pages/view/profile/view-profile.page';

import { ChangedResolver } from './resolvers/changed.resolver';
import { DisinvestmentResolver } from './resolvers/disinvestment.resolver';
import { FeeResolver } from './resolvers/fee.resolver';
import { TransferResolver } from './resolvers/transfer.resolver';

const routes: Routes = [
    {
        path: 'assignment',
        component: AssignmentPage,
        canActivate: [UserAuthenticated],
        children: [
            {
                path: '',
                redirectTo: 'operate',
                pathMatch: 'full',
            },
            {
                path: 'operate',
                component: AssignmentOperatePage,
            },
            {
                path: 'sign',
                component: AssignmentSignPage,
                resolve: {
                    fee: FeeResolver,
                },
            },
        ],
    },
    {
        path: 'contribution',
        component: ContributionPage,
        canActivate: [UserAuthenticated],
        children: [
            {
                path: '',
                redirectTo: 'operate',
                pathMatch: 'full',
            },
            {
                path: 'operate',
                component: ContributionOperatePage,
            },
            {
                path: 'sign',
                component: ContributionSignPage,
                resolve: {
                    changed: ChangedResolver,
                    transfer: TransferResolver,
                },
            },
        ],
    },
    {
        path: 'disinvestment',
        component: DisinvestmentPage,
        canActivate: [UserAuthenticated],
        children: [
            {
                path: '',
                redirectTo: 'operate',
                pathMatch: 'full',
            },
            {
                path: 'operate',
                component: DisinvestmentOperatePage,
            },
            {
                path: 'sign',
                component: DisinvestmentSignPage,
                resolve: {
                    changed: ChangedResolver,
                    disinvestment: DisinvestmentResolver,
                },
            },
        ],
    },
    {
        path: 'dictionary',
        component: DictionaryDialog,
    },
    {
        path: 'error/unauthorized',
        component: UnauthorizedPage,
    },
    {
        path: 'error/not-found',
        component: NotFoundPage,
    },
    {
        path: 'onboarding/full-data',
        component: FullDataPage,
        canActivate: [UserAuthenticated],
        children: [
            {
                path: '',
                redirectTo: 'list',
                pathMatch: 'full',
            },
            {
                path: 'account',
                component: FullDataAccountPage,
            },
            {
                path: 'list',
                component: FullDataListPage,
            },
            {
                path: 'list/:id',
                component: FullDataProfilePage,
            },
        ],
    },
    {
        path: 'onboarding/new',
        component: ContractPage,
        children: [
            {
                path: '',
                redirectTo: 'nature',
                pathMatch: 'full',
            },
            {
                path: 'intro',
                component: ContractLandingPage,
            },
            {
                path: 'nature',
                component: ContractNaturePage,
            },
            {
                path: 'natural',
                component: ContractNaturalPage,
            },
            {
                path: 'minor-disabled',
                component: ContractMinorDisabledPage,
            },
            {
                path: 'form',
                component: ContractFormPage,
            },
            {
                path: 'participants',
                component: ContractParticipantsPage,
            },
            {
                path: 'sign',
                component: ContractSignPage,
            },
            {
                path: 'advice',
                component: ContractAdvicePage,
            },
        ],
    },
    {
        path: 'onboarding/test',
        component: TestPage,
        canActivate: [UserAuthenticated],
        children: [
            {
                path: '',
                redirectTo: '0',
                pathMatch: 'full',
            },
            {
                path: 'result',
                component: TestResultPage,
            },
            {
                path: 'result-suitability',
                component: TestResultSuitabilityPage,
            },
            {
                path: 'rate',
                component: TestRatePage,
            },
            {
                path: 'transfer',
                component: TestTransferPage,
            },
            {
                path: ':question',
                component: TestQuestionPage,
            },
        ],
    },
    {
        path: 'operative',
        component: OperativePage,
        canActivate: [UserAuthenticated],
        children: [
            {
                path: '',
                redirectTo: 'list',
                pathMatch: 'full',
            },
            {
                path: 'list',
                component: OperativeListPage,
            },
            {
                path: 'sign',
                component: OperativeSignPage,
            },
            {
                path: 'meeting-sign',
                component: MeetingSignPage,
            },
            {
                path: 'operate',
                component: OperativeOperatePage,
            },
            {
                path: 'products/list',
                component: OperativeProductListPage,
            },
            {
                path: 'products/my-list',
                component: OperativeProductListPage,
            },
            {
                path: 'products/:id',
                component: OperativeProductDetailPage,
            },
        ],
    },
    {
        path: 'phone',
        component: PhoneDialog,
    },
    {
        path: 'repeat/test',
        component: RepeatTestPage,
        canActivate: [UserAuthenticated],
        children: [
            {
                path: '',
                redirectTo: '0',
                pathMatch: 'full',
            },
            {
                path: 'result',
                component: RepeatTestResultPage,
            },
            {
                path: 'result-suitability',
                component: RepeatTestResultSuitabilityPage,
            },
            {
                path: 'sign',
                component: RepeatTestSignPage,
            },
            {
                path: 'sign/handwritten',
                component: RepeatTestSignHandwritingPage,
            },
            {
                path: ':question',
                component: RepeatTestQuestionPage,
            },
        ],
    },
    {
        path: 'view',
        component: ViewPage,
        canActivate: [UserAuthenticated],
        children: [
            {
                path: '',
                redirectTo: '/onboarding/new',
                pathMatch: 'full',
            },
            {
                path: 'account',
                component: ViewAccountPage,
            },
            {
                path: 'profile/:id',
                component: ViewProfilePage,
            },
        ],
    },
    {
        path: '**',
        redirectTo: 'error/not-found',
        pathMatch: 'full',
    },
];

@NgModule({
    imports: [RouterModule.forChild(routes)],
    exports: [RouterModule],
})
export class MainRoutingModule {}
